import type { ReactNode } from "react";
import type { AgentMetricsSnapshot, AgentRunRecord, MetricsWindow } from "../utils/types";
import { ActiveRunsPanel } from "./ActiveRunsPanel";
import { DualAxisChart } from "./BarChart";
import { OverviewStatCards } from "./OverviewStatCards";
import { RecentFailuresPanel } from "./RecentFailuresPanel";
import { StatusBreakdown } from "./StatusBreakdown";

type OverviewSectionProps = {
  metrics: AgentMetricsSnapshot | null;
  loading: boolean;
  window: MetricsWindow;
  onWindowChange: (window: MetricsWindow) => void;
  activeRuns: AgentRunRecord[];
  onSelectFailure: (runId: string) => void;
};

type ChartCardProps = {
  title: string;
  legend?: { bar: string; line?: string };
  children: ReactNode;
};

const ChartCard = ({ title, legend, children }: ChartCardProps) => (
  <section className="admin-chart-card">
    <div className="admin-chart-header">
      <h3 className="admin-chart-title">{title}</h3>
      {legend ? (
        <div className="admin-chart-legend">
          <span><span className="admin-legend-bar" /> {legend.bar}</span>
          {legend.line ? (
            <span><span className="admin-legend-line" /> {legend.line}</span>
          ) : null}
        </div>
      ) : null}
    </div>
    {children}
  </section>
);

export const OverviewSection = ({
  metrics,
  loading,
  window,
  onWindowChange,
  activeRuns,
  onSelectFailure,
}: OverviewSectionProps) => {
  const buckets = metrics?.buckets ?? [];
  const completed = buckets.map((b) => b.completed);
  const failed = buckets.map((b) => b.failed + b.fallback);
  const started = buckets.map((b) => b.started);
  const total = buckets.map((b) => b.started + b.completed + b.failed + b.fallback);

  return (
    <section className="admin-section">
      <div className="admin-section-header">
        <h2 className="admin-section-title">Overview</h2>
        <select
          className="admin-control"
          value={window}
          onChange={(event) => onWindowChange(event.target.value as MetricsWindow)}
        >
          <option value="1h">Past hour</option>
          <option value="24h">Past 24 hours</option>
        </select>
      </div>

      <OverviewStatCards metrics={metrics} />

      <div className="admin-overview-secondary">
        <StatusBreakdown metrics={metrics} />
      </div>

      <div className="admin-charts-grid">
        <ChartCard title="Runs" legend={{ bar: "completed", line: "failed" }}>
          <DualAxisChart barValues={completed} lineValues={failed} />
        </ChartCard>
        <ChartCard title="Throughput" legend={{ bar: "total" }}>
          <DualAxisChart barValues={total} lineValues={started} lineColor="var(--chart-blue)" />
        </ChartCard>
        <ChartCard title="Failures" legend={{ bar: "failed", line: "fallback" }}>
          <DualAxisChart
            barValues={buckets.map((b) => b.failed)}
            lineValues={buckets.map((b) => b.fallback)}
            barColor="var(--chart-red)"
            lineColor="#f97316"
          />
        </ChartCard>
        <ChartCard title="Active" legend={{ bar: "started" }}>
          <DualAxisChart
            barValues={started}
            lineValues={[]}
            barColor="var(--chart-blue)"
          />
        </ChartCard>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <ActiveRunsPanel runs={activeRuns} loading={loading} />
        <RecentFailuresPanel
          runs={metrics?.recentFailures ?? []}
          onSelectRun={onSelectFailure}
        />
      </div>
    </section>
  );
};
